import React from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { LoginForm } from "../components/LoginForm";
// import { LOGO_PATH, LOGO_ALT } from "../../../../constants/logo";

export const LoginPage: React.FC = () => {
  const {
    login,
    isAuthenticated,
    loading,
    error,
    clearError,
    isMasterAdmin,
    needsPasswordChange,
  } = useAuth();
  const navigate = useNavigate();

  const handleLogin = async (email: string, password: string) => {
    clearError();

    try {
      const result = await login({ email, password });
      if (result.success) {
        // Check if password needs to be changed
        if (needsPasswordChange()) {
          navigate("/change-password");
          return;
        }

        // Check if master admin
        if (isMasterAdmin()) {
          navigate("/admin/dashboard");
        } else {
          navigate("/admin/companies/select");
        }
      }
    } catch (error) {
      console.error("Login error:", error);
    }
  };

  // Show loading while checking authentication
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // If user is authenticated, redirect based on role and password change status
  if (isAuthenticated) {
    // Check if password needs to be changed
    if (needsPasswordChange()) {
      return <Navigate to="/change-password" replace />;
    }

    if (isMasterAdmin()) {
      return <Navigate to="/admin/dashboard" replace />;
    } else {
      return <Navigate to="/admin/companies/select" replace />;
    }
  }

  // Show login form
  return (
    <div className="flex h-screen w-full flex-col">
      <div className="flex h-full w-full flex-wrap items-start bg-default-background mobile:flex-col mobile:flex-wrap mobile:gap-0">
        {/* Left Side - Branding */}
        <div className="flex max-w-[576px] grow shrink-0 basis-0 flex-col items-center gap-12 self-stretch bg-neutral-800 px-12 py-12 mobile:h-auto mobile:w-full mobile:flex-none">
          <div className="flex w-full max-w-[448px] grow shrink-0 basis-0 flex-col items-start justify-center gap-12 mobile:h-auto mobile:w-full mobile:max-w-[448px] mobile:flex-none">
            {/* <img
              alt={LOGO_ALT}
              className="h-8 flex-none object-cover invert"
              src={LOGO_PATH}
            /> */}
            <div className="flex flex-col items-start justify-center gap-16 pb-32 mobile:px-0 mobile:py-0">
              <div className="flex flex-col items-start gap-2">
                <span className="text-heading-2 font-heading-2 text-white">
                  Welcome back
                </span>
                <span className="text-heading-3 font-heading-3 text-brand-200">
                  Sign in to access your account and continue your work.
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="flex grow shrink-0 basis-0 flex-col items-center justify-center gap-6 self-stretch border-l border-solid border-neutral-border px-12 py-12">
          <LoginForm onSubmit={handleLogin} isLoading={loading} error={error} />
        </div>
      </div>
    </div>
  );
};
